import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectFilters } from '../components/projects/ProjectFilters';
import { Card } from '../components/ui/Card';
import { StatusPill } from '../components/ui/StatusPill';
import { MapPinIcon, BanknoteIcon, CalendarIcon, StarIcon } from 'lucide-react';
import { projects } from '../data/projects';
export function Projects() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('All Sectors');
  const [statusFilter, setStatusFilter] = useState('all');
  const sectors = useMemo(() => Array.from(new Set(projects.map(p => p.sector))).sort(), []);
  const featuredProject = projects.find(p => p.featured);
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) || project.description.toLowerCase().includes(searchQuery.toLowerCase()) || project.countries.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSector = sectorFilter === 'All Sectors' || project.sector === sectorFilter;
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesSector && matchesStatus;
  });
  return <div data-id="element-755">
      <PageHero title="Our Projects" subtitle="Transformative initiatives advancing sustainable development across Africa through global partnerships" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Projects'
    }]} data-id="element-756" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-757">
        {featuredProject && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-758">
            <div className="flex items-center gap-2 mb-4" data-id="element-759">
              <StarIcon size={20} className="text-au-gold fill-au-gold" data-id="element-760" />
              <h2 className="text-2xl font-serif text-charcoal" data-id="element-761">
                Featured Project
              </h2>
            </div>

            <Card className="border-2 border-au-green overflow-hidden p-0" data-id="element-762">
              <div className="grid md:grid-cols-2" data-id="element-763">
                <div className="bg-au-green p-8 md:p-10 text-white flex flex-col justify-center" data-id="element-764">
                  <span className="inline-block bg-au-gold text-charcoal text-xs font-bold px-3 py-1 rounded-full mb-4 self-start" data-id="element-765">
                    {featuredProject.sector.toUpperCase()}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-serif mb-4 leading-tight" data-id="element-766">
                    {featuredProject.title}
                  </h3>
                  <div className="w-16 h-1 bg-au-gold mb-4" data-id="element-767"></div>
                  <p className="text-au-green-light text-lg leading-relaxed" data-id="element-768">
                    {featuredProject.description}
                  </p>
                </div>

                <div className="p-8 md:p-10 flex flex-col justify-center bg-white" data-id="element-769">
                  <div className="mb-6" data-id="element-770">
                    <StatusPill status={featuredProject.status} data-id="element-771">
                      {featuredProject.status.toUpperCase()}
                    </StatusPill>
                  </div>

                  <div className="space-y-5" data-id="element-772">
                    <div className="flex items-start gap-3" data-id="element-773">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-774">
                        <MapPinIcon size={20} className="text-au-green" data-id="element-775" />
                      </div>
                      <div data-id="element-776">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-777">
                          Countries
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-778">
                          {featuredProject.countries.join(', ')}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-779">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-780">
                        <BanknoteIcon size={20} className="text-au-green" data-id="element-781" />
                      </div>
                      <div data-id="element-782">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-783">
                          Total Budget
                        </div>
                        <div className="font-bold text-au-green text-lg" data-id="element-784">
                          {featuredProject.budget}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-785">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-786">
                        <CalendarIcon size={20} className="text-au-green" data-id="element-787" />
                      </div>
                      <div data-id="element-788">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-789">
                          Timeline
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-790">
                          {new Date(featuredProject.startDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}
                          {featuredProject.endDate && ` – ${new Date(featuredProject.endDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>}

        <div className="mb-8" data-id="element-791">
          <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-792">
            All Projects
          </h2>
          <div className="w-16 h-1 bg-au-gold" data-id="element-793"></div>
        </div>

        <ProjectFilters searchQuery={searchQuery} setSearchQuery={setSearchQuery} sectorFilter={sectorFilter} setSectorFilter={setSectorFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} sectors={sectors} data-id="element-794" />

        <div className="mb-6" data-id="element-795">
          <p className="text-gray-600" data-id="element-796">
            Showing{' '}
            <span className="font-bold text-charcoal" data-id="element-797">
              {filteredProjects.length}
            </span>{' '}
            project{filteredProjects.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-798">
          {filteredProjects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} data-id="element-799" />)}
        </div>

        {filteredProjects.length === 0 && <div className="text-center py-16 bg-white rounded-lg border border-gray-border" data-id="element-800">
            <p className="text-gray-600 text-lg mb-2" data-id="element-801">
              No projects match your filters.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSectorFilter('All Sectors');
          setStatusFilter('all');
        }} className="text-au-green font-medium hover:underline" data-id="element-802">
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}